import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import HistoryIcon from '@mui/icons-material/History'
import SearchIcon from '@mui/icons-material/Search'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import StatusChip, { MEMBERSHIP_STATUS_CHIP } from '../../component/StatusChip'
import API from '../../component/Axios'
import { renderDateTime } from '../../utils/dateDisplay'
import { formatMembershipPaidAmount, getMembershipDisplayStatus } from './membershipDisplay'

const LOOKUP_TYPES = [
    { value: 'userId', label: '회원 ID', requiresStore: false },
    { value: 'purchaseToken', label: 'Google purchaseToken', requiresStore: true },
    { value: 'originalTransactionId', label: 'Apple originalTransactionId', requiresStore: true },
    { value: 'transactionId', label: 'transactionId', requiresStore: true },
    { value: 'storeOrderId', label: 'storeOrderId', requiresStore: true },
]

const DEFAULT_LOOKUP_FORM = {
    lookupType: 'userId',
    store: 'google',
    value: '',
    reason: 'CS 원계정 확인',
}

const DEFAULT_LOG_FILTERS = {
    targetId: '',
    adminId: '',
}

const sectionSx = {
    border: 1,
    borderColor: 'divider',
    borderRadius: 1,
    p: 2,
    height: '100%',
}

const fieldSx = {
    minWidth: { xs: '100%', sm: 180 },
}

const monoTextSx = {
    fontFamily: 'monospace',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
}

const getLookupTypeConfig = (lookupType) => LOOKUP_TYPES.find((item) => item.value === lookupType) || LOOKUP_TYPES[0]

const safeObject = (value) => {
    if (!value) return {}
    if (typeof value === 'object') return value

    try {
        return JSON.parse(value)
    } catch (_error) {
        return {}
    }
}

const formatPlanType = (planType) => {
    if (planType === 'monthly') return '월간'
    if (planType === 'yearly') return '연간'
    return planType || '-'
}

const renderIdentifierSummary = (identifiers = {}) => {
    const entries = Object.entries(identifiers)

    if (entries.length === 0) return '-'

    return entries.map(([field, summary]) => (
        <Box key={field} sx={{ mb: 0.5 }}>
            <Typography component="span" variant="caption" color="text.secondary">{field}</Typography>
            <Typography variant="body2" sx={monoTextSx}>
                {summary?.last4 ? `last4: ${summary.last4}` : '-'}
            </Typography>
        </Box>
    ))
}

const Field = ({ label, value }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '120px 1fr' }, gap: 1, py: 0.5, borderBottom: '1px solid rgba(148, 163, 184, 0.18)' }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="body2" component="div">{value || '-'}</Typography>
    </Box>
)

const OwnershipResult = ({ data, onOpenDetail }) => {
    if (!data) return null

    if (!data.found) {
        return (
            <Box sx={{ mt: 2 }}>
                <ListEmptyState
                    title="연결된 구독 계정을 찾지 못했습니다."
                    description="입력한 식별자와 스토어를 다시 확인해주세요."
                />
            </Box>
        )
    }

    const owner = data.owner || {}
    const user = owner.user || {}
    const membership = owner.membership || {}
    const purchases = owner.purchases || []

    return (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Typography variant="subtitle2">조회 결과</Typography>
                <Chip size="small" color="warning" label="구독 이전 불가" />
            </Box>
            <Field label="회원" value={`${user.nickNameMasked || user.usernameMasked || '-'} (${user.userNo || user.userId || '-'})`} />
            <Field label="휴대폰" value={user.phoneMasked} />
            <Field label="이메일" value={user.emailMasked} />
            <Field label="SNS" value={(owner.snsProviders || []).map((provider) => provider.provider).join(', ') || '-'} />
            <Field label="멤버십" value={
                membership.membershipId ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <StatusChip status={getMembershipDisplayStatus(membership)} config={MEMBERSHIP_STATUS_CHIP} />
                        <Typography variant="body2">{membership.store || '-'} / {formatPlanType(membership.plan?.planType)}</Typography>
                        <Button size="small" variant="outlined" onClick={() => onOpenDetail(membership.membershipId)}>상세</Button>
                    </Box>
                ) : '-'
            } />
            {membership.membershipId && getMembershipDisplayStatus(membership) !== membership.status && (
                <Field label="원장 상태" value={<StatusChip status={membership.status} config={MEMBERSHIP_STATUS_CHIP} />} />
            )}
            <Field label="혜택 종료" value={renderDateTime(membership.benefitUntil)} />
            <Field label="식별자" value={renderIdentifierSummary(membership.identifiers)} />

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>최근 구매 원장</Typography>
            <ListTableContainer>
                <Table size="small" sx={{ minWidth: 720 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>상태</TableCell>
                            <TableCell>상품</TableCell>
                            <TableCell>결제금액</TableCell>
                            <TableCell>구매일</TableCell>
                            <TableCell>만료일</TableCell>
                            <TableCell>식별자</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {purchases.map((purchase) => (
                            <TableRow key={purchase.purchaseId}>
                                <TableCell>{purchase.purchaseId}</TableCell>
                                <TableCell>{purchase.status}</TableCell>
                                <TableCell>{formatPlanType(purchase.plan?.planType)}</TableCell>
                                <TableCell>{formatMembershipPaidAmount(purchase)}</TableCell>
                                <TableCell>{renderDateTime(purchase.purchasedAt)}</TableCell>
                                <TableCell>{renderDateTime(purchase.expiresAt)}</TableCell>
                                <TableCell>{renderIdentifierSummary(purchase.identifiers)}</TableCell>
                            </TableRow>
                        ))}
                        {purchases.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7}>구매 원장이 없습니다.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </ListTableContainer>
        </Box>
    )
}

const OperationLogs = ({ rows, searched }) => {
    if (!searched) return null

    if (rows.length === 0) {
        return (
            <Box sx={{ mt: 2 }}>
                <ListEmptyState
                    title="감사 로그가 없습니다."
                    description="조회 조건을 바꿔 다시 확인해주세요."
                />
            </Box>
        )
    }

    return (
        <ListTableContainer sx={{ mt: 2 }}>
            <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                    <TableRow>
                        <TableCell>시간</TableCell>
                        <TableCell>관리자</TableCell>
                        <TableCell>대상</TableCell>
                        <TableCell>결과</TableCell>
                        <TableCell>사유</TableCell>
                        <TableCell>조회 기준</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => {
                        const beforeValue = safeObject(row.beforeValue)
                        const afterValue = safeObject(row.afterValue)
                        const identifierFields = Object.keys(beforeValue.identifierHashes || {})

                        return (
                            <TableRow key={row.logId}>
                                <TableCell>{renderDateTime(row.regTime)}</TableCell>
                                <TableCell>{row.adminId || '-'}</TableCell>
                                <TableCell>{row.targetId || '-'}</TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        color={afterValue.found ? 'success' : 'default'}
                                        label={afterValue.found ? '매칭' : '미매칭'}
                                    />
                                </TableCell>
                                <TableCell>{row.reason || '-'}</TableCell>
                                <TableCell>
                                    {beforeValue.userId ? `userId: ${beforeValue.userId}` : identifierFields.join(', ') || '-'}
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </ListTableContainer>
    )
}

export default function MembershipSupportTools() {
    const history = useHistory()
    const [lookupForm, setLookupForm] = useState(DEFAULT_LOOKUP_FORM)
    const [lookupLoading, setLookupLoading] = useState(false)
    const [lookupErrorMessage, setLookupErrorMessage] = useState('')
    const [lookupResult, setLookupResult] = useState(null)
    const [logFilters, setLogFilters] = useState(DEFAULT_LOG_FILTERS)
    const [logLoading, setLogLoading] = useState(false)
    const [logErrorMessage, setLogErrorMessage] = useState('')
    const [logRows, setLogRows] = useState([])
    const [logSearched, setLogSearched] = useState(false)

    const lookupConfig = getLookupTypeConfig(lookupForm.lookupType)

    const updateLookupForm = (field) => (event) => {
        setLookupForm((prevForm) => ({
            ...prevForm,
            [field]: event.target.value,
        }))
    }

    const updateLogFilters = (field) => (event) => {
        setLogFilters((prevFilters) => ({
            ...prevFilters,
            [field]: event.target.value,
        }))
    }

    const handleUnauthorized = (code) => {
        if (code === 401 || code === 403) {
            history.push('/login')
            return true
        }

        return false
    }

    const searchOwnership = async (event) => {
        event.preventDefault()

        const value = lookupForm.value.trim()
        const reason = lookupForm.reason.trim()

        if (!value) {
            setLookupErrorMessage('조회할 값을 입력해주세요.')
            return
        }

        if (lookupConfig.requiresStore && !lookupForm.store) {
            setLookupErrorMessage('스토어 식별자 조회에는 store가 필요합니다.')
            return
        }

        const payload = lookupConfig.value === 'userId'
            ? { userId: value, reason }
            : { store: lookupForm.store, [lookupConfig.value]: value, reason }

        setLookupLoading(true)
        setLookupErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/membership/lookupOwnership', payload)

            if (data.code === 200) {
                setLookupResult(data.data)

                const ownerUserId = data.data?.owner?.user?.userId || data.data?.lookup?.userId
                if (ownerUserId) {
                    setLogFilters((prevFilters) => ({
                        ...prevFilters,
                        targetId: String(ownerUserId),
                    }))
                }
            } else if (!handleUnauthorized(data.code)) {
                setLookupResult(null)
                setLookupErrorMessage(data.message || '구독 원계정 조회에 실패했습니다.')
            }
        } catch (_error) {
            setLookupResult(null)
            setLookupErrorMessage('구독 원계정 조회에 실패했습니다.')
        } finally {
            setLookupLoading(false)
        }
    }

    const searchOperationLogs = async (event) => {
        event.preventDefault()

        setLogLoading(true)
        setLogErrorMessage('')
        setLogSearched(true)

        const payload = {
            page: 1,
            offset: 20,
            action: 'membership_ownership_lookup',
        }

        if (logFilters.targetId.trim()) {
            payload.targetId = logFilters.targetId.trim()
        }

        if (logFilters.adminId.trim()) {
            payload.adminId = logFilters.adminId.trim()
        }

        try {
            const { data } = await API.post('/api/v1/admin/membership/getOperationLogs', payload)

            if (data.code === 200) {
                setLogRows(data.data?.rows || [])
            } else if (!handleUnauthorized(data.code)) {
                setLogRows([])
                setLogErrorMessage(data.message || '감사 로그를 불러오지 못했습니다.')
            }
        } catch (_error) {
            setLogRows([])
            setLogErrorMessage('감사 로그를 불러오지 못했습니다.')
        } finally {
            setLogLoading(false)
        }
    }

    const openMembershipDetail = (membershipId) => {
        history.push(`/membership/${membershipId}`)
    }

    return (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 2, mb: 2 }}>
            <Typography variant="subtitle1" component="h2" sx={{ mb: 1.5 }}>CS 구독 조회</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} lg={5}>
                    <Box sx={sectionSx}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>구독 원계정 조회</Typography>
                        <Box component="form" onSubmit={searchOwnership} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <TextField
                                select
                                size="small"
                                label="조회 기준"
                                value={lookupForm.lookupType}
                                onChange={updateLookupForm('lookupType')}
                                sx={fieldSx}
                            >
                                {LOOKUP_TYPES.map((item) => (
                                    <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                                ))}
                            </TextField>
                            {lookupConfig.requiresStore && (
                                <TextField
                                    select
                                    size="small"
                                    label="스토어"
                                    value={lookupForm.store}
                                    onChange={updateLookupForm('store')}
                                    sx={{ minWidth: { xs: '100%', sm: 120 } }}
                                >
                                    <MenuItem value="google">Google</MenuItem>
                                    <MenuItem value="apple">Apple</MenuItem>
                                </TextField>
                            )}
                            <TextField
                                size="small"
                                label="조회값"
                                value={lookupForm.value}
                                onChange={updateLookupForm('value')}
                                sx={{ flex: 1, minWidth: { xs: '100%', sm: 220 } }}
                            />
                            <TextField
                                size="small"
                                label="사유"
                                value={lookupForm.reason}
                                onChange={updateLookupForm('reason')}
                                sx={{ flex: 1, minWidth: { xs: '100%', sm: 180 } }}
                            />
                            <Button type="submit" size="small" variant="contained" startIcon={<SearchIcon />} disabled={lookupLoading}>
                                조회
                            </Button>
                        </Box>
                        {lookupErrorMessage && (
                            <Typography variant="body2" color="error" sx={{ mt: 1 }}>{lookupErrorMessage}</Typography>
                        )}
                        <OwnershipResult data={lookupResult} onOpenDetail={openMembershipDetail} />
                    </Box>
                </Grid>
                <Grid item xs={12} lg={7}>
                    <Box sx={sectionSx}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>소유권 조회 감사 로그</Typography>
                        <Box component="form" onSubmit={searchOperationLogs} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <TextField
                                size="small"
                                label="대상 회원 ID"
                                value={logFilters.targetId}
                                onChange={updateLogFilters('targetId')}
                                sx={fieldSx}
                            />
                            <TextField
                                size="small"
                                label="관리자 ID"
                                value={logFilters.adminId}
                                onChange={updateLogFilters('adminId')}
                                sx={fieldSx}
                            />
                            <Button type="submit" size="small" variant="outlined" startIcon={<HistoryIcon />} disabled={logLoading}>
                                로그 조회
                            </Button>
                        </Box>
                        {logErrorMessage && (
                            <Typography variant="body2" color="error" sx={{ mt: 1 }}>{logErrorMessage}</Typography>
                        )}
                        <OperationLogs rows={logRows} searched={logSearched} />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    )
}
